'use client';

import { useState } from 'react';
import { MailWarning, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { resendVerificationEmail } from '@/lib/api';
import { VERIFICATION_NOTES, type VerificationNote } from '@/lib/verificationCopy';
import { cn } from '@/lib/utils';

/**
 * Alert emails only go to verified addresses, and go nowhere at all otherwise.
 * Surface that wherever alert expectations are set — the dashboard, saved
 * searches, and the search filter panel.
 *
 * **This is the surface that matters.** `/settings/profile` is where somebody
 * goes when they already suspect a problem; this banner is where they find out
 * there is one. Fixing only the profile page would have been half a fix, and
 * the half nobody looks at.
 *
 * Two things were wrong here, both of them the same shape:
 *
 * 1. **It read the wrong flag.** `firebaseUser.emailVerified` is a claim cached
 *    in a Firebase ID token. The gate alert email is actually subject to is
 *    `users.email_verified` in our own database, which is what `user` carries.
 *    They disagree in both directions: the token goes stale until it refreshes,
 *    and it stays `false` forever for anyone who verified through a GoReal
 *    link. So this banner could hide itself from somebody receiving nothing, or
 *    nag somebody who was already fine.
 * 2. **It sent a Firebase email and then said so regardless.** The resend now
 *    goes through `POST /api/v1/me/resend-verification`, the endpoint that
 *    writes the column, and every one of its five outcomes is reported from the
 *    shared table — so "check your inbox" appears only when the provider
 *    actually accepted the message.
 */
export default function VerifyEmailNudge({ compact = false }: { compact?: boolean }) {
  const user = useAuthStore((s) => s.user);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<VerificationNote | null>(null);

  // Signed out, or verified as far as OUR records go — which is the only
  // opinion that decides whether an alert email is sent.
  if (!user || user.email_verified === true) return null;

  const resend = async () => {
    setBusy(true);
    setNote(null);
    // Resolves for every outcome, so there is no `catch` and no `finally` that
    // can put a success line on screen without the server having said `sent`.
    const result = await resendVerificationEmail();
    setNote(VERIFICATION_NOTES[result.status]);
    setBusy(false);
  };

  const done = note !== null;

  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-2.5 rounded-xl border',
        note?.tone === 'bad'
          ? 'border-coral-500/30 bg-coral-500/10'
          : 'border-gold-500/30 bg-gold-500/10',
        compact ? 'px-3 py-2 text-xs' : 'p-3.5 text-sm'
      )}
    >
      {note === null ? (
        <MailWarning className="h-4 w-4 flex-shrink-0 text-gold-600" />
      ) : note.tone === 'ok' ? (
        <CheckCircle className="h-4 w-4 flex-shrink-0 text-emerald-700" />
      ) : (
        <AlertCircle className="h-4 w-4 flex-shrink-0 text-coral-600" />
      )}

      <span className="text-ink-soft">
        {note?.message ?? 'Email alerts pause until you verify your address.'}
      </span>

      {/* A failed send must leave the button there — the whole point of saying
          "nothing was sent" is that there is still something to try. Only a
          genuinely accepted message (or an address that turned out to be
          verified already) retires it. */}
      {(!done || note.tone === 'bad') && (
        <button
          type="button"
          onClick={resend}
          disabled={busy}
          className="font-semibold text-emerald-700 underline-offset-2 hover:underline disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : done ? (
            'Try again'
          ) : (
            'Resend verification'
          )}
        </button>
      )}
    </div>
  );
}
